import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Autocomplete from '../components/ui/Autocomplete';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../layouts/MainLayout';
import ApiService from '../services/api';
import { Download, Filter, RefreshCw, TrendingUp, Users, Phone, Clock, CheckCircle } from 'lucide-react';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface DashboardReport {
  overall_stats: {
    total_calls: number;
    completed_calls: number;
    missed_calls: number;
    rejected_calls: number;
    in_progress_calls: number;
    completion_rate: number;
    missed_rate: number;
    rejection_rate: number;
    in_progress_rate: number;
  };
  top_agents_by_calls: Array<{
    id: number;
    name: string;
    email: string;
    metric_value: number;
    completed_calls: number;
    completion_rate: number;
  }>;

  recent_trends: Array<{
    period: string;
    total_calls: number;
    completed_calls: number;
    missed_calls: number;
    avg_duration: number;
  }>;
  status_distribution: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  duration_analytics: {
    statistics: {
      min_duration: number;
      max_duration: number;
      avg_duration: number;
      std_duration: number;
      total_calls: number;
      short_calls: number;
      medium_calls: number;
      long_calls: number;
    };
    distribution: Array<{
      duration_range: string;
      count: number;
    }>;
  };
  date_range: {
    from: string;
    to: string;
  };
}

interface Agent {
  id: number;
  name: string;
  email: string;
}

const today = new Date();
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
const formatDateInput = (date: Date) => date.toISOString().slice(0, 10);

const AgentPerformanceReport: React.FC = () => {
  const { token } = useAuth();
  const [report, setReport] = useState<DashboardReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(formatDateInput(firstDayOfMonth));
  const [dateTo, setDateTo] = useState(formatDateInput(today));
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [agentSearchValue, setAgentSearchValue] = useState('');

  const searchAgents = async (query: string): Promise<Agent[]> => {
    if (!token) return [];

    try {
      const response = await ApiService.get<Agent[]>('agent-performance-report/search-agents', { query });
      return response.data;
    } catch (error) {
      console.error('Error searching agents:', error);
      return [];
    }
  };

  const fetchDashboardReport = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (selectedAgent) params.agent_id = selectedAgent.id;

      const response = await ApiService.get<DashboardReport>('agent-performance-report/dashboard-report', params);
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching dashboard report:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format: 'csv' | 'json' = 'csv') => {
    if (!token) return;

    try {
      const params: Record<string, any> = { format };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (selectedAgent) params.agent_id = selectedAgent.id;

      const response = await ApiService.get('agent-performance-report/export-call-report', params);

      // Cast response.data to the expected type
      const data = response.data as { data: any[]; filename: string; format: string };

      if (format === 'csv') {
        // Create and download CSV file
        const csvContent = data.data.map((row: any[]) => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        // Download JSON file
        const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting report:', error);
    }
  };

  useEffect(() => {
    fetchDashboardReport();
  }, [token, dateFrom, dateTo, selectedAgent]);

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const durationChartData = {
    labels: report?.duration_analytics.distribution.map(d => d.duration_range) || [],
    datasets: [
      {
        label: 'Number of Calls',
        data: report?.duration_analytics.distribution.map(d => d.count) || [],
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1,
      },
    ],
  };

  // Top Agents by Call Volume Bar Chart Data
  const topAgents = report?.top_agents_by_calls.slice(0, 10) || [];
  const topAgentsBarData = {
    labels: topAgents.map(agent => agent.name),
    datasets: [
      {
        label: 'Calls',
        data: topAgents.map(agent => agent.metric_value),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1,
      },
    ],
  };

  return (
    <MainLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Agent Performance Report
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Comprehensive analytics and performance metrics for call center agents
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center w-full lg:w-auto">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateFrom(e.target.value)}
                  className="w-full sm:w-40"
                  placeholder="From Date"
                />
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateTo(e.target.value)}
                  className="w-full sm:w-40"
                  placeholder="To Date"
                />
                <Autocomplete<Agent>
                  placeholder="Search agents..."
                  value={agentSearchValue}
                  onChange={setAgentSearchValue}
                  onSelect={(agent) => setSelectedAgent(agent)}
                  searchFunction={searchAgents}
                  debounceMs={300}
                  className="w-full"
                  style={{ minWidth: '200px' }}
                  displayKey="name"
                  subDisplayKey="email"
                />
              </div>
            </div>
            <div className="flex gap-2 w-full lg:w-auto">
              <Button onClick={fetchDashboardReport} disabled={loading} className="flex items-center gap-2 w-full sm:w-auto">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
              <Button onClick={() => exportReport('csv')} className="flex items-center gap-2 w-full sm:w-auto">
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-500 mb-4" />
            <p className="text-gray-500">Loading comprehensive report...</p>
          </div>
        ) : report ? (
          <>
            {/* Overall Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <Phone className="w-8 h-8 text-blue-500 mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Calls</h3>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{report.overall_stats.total_calls}</div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Completion Rate</h3>
                    <div className="text-2xl font-bold text-green-600">{report.overall_stats.completion_rate}%</div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <Clock className="w-8 h-8 text-purple-500 mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Avg Duration</h3>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {formatDuration(Math.round(report.duration_analytics.statistics.avg_duration))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <Users className="w-8 h-8 text-orange-500 mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Agents</h3>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{report.top_agents_by_calls.length}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Duration Analytics */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow mb-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Call Duration Analytics</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <Bar
                    data={durationChartData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: {
                          display: false,
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                        },
                      },
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{report.duration_analytics.statistics.short_calls}</div>
                    <div className="text-sm text-gray-500">Short Calls (&lt;1 min)</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{report.duration_analytics.statistics.medium_calls}</div>
                    <div className="text-sm text-gray-500">Medium Calls (1-5 min)</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">{report.duration_analytics.statistics.long_calls}</div>
                    <div className="text-sm text-gray-500">Long Calls (&gt;5 min)</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">{formatDuration(Math.round(report.duration_analytics.statistics.avg_duration))}</div>
                    <div className="text-sm text-gray-500">Average Duration</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Performing Agents */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Top Agents by Calls Table (left) */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Agents by Call Volume</h3>
                </div>
                <div className="p-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Agent</TableHead>
                        <TableHead>Calls</TableHead>
                        <TableHead>Completion Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {topAgents.map((agent, index) => (
                        <TableRow key={agent.id}>
                          <TableCell>
                            <div>
                              <div className="font-medium">{agent.name}</div>
                              <div className="text-sm text-gray-500">{agent.email}</div>
                            </div>
                          </TableCell>
                          <TableCell className="font-bold">{agent.metric_value}</TableCell>
                          <TableCell>
                            <span className="text-green-600 font-medium">{agent.completion_rate}%</span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              {/* Top Agents by Call Volume Bar Chart (right) */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow flex flex-col justify-center">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Top Agents by Call Volume (Chart)</h3>
                </div>
                <div className="p-6 flex-1 flex items-center justify-center">
                  <Bar
                    data={topAgentsBarData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { display: false },
                      },
                      scales: {
                        y: { beginAtZero: true },
                      },
                    }}
                    height={260}
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">No data available for the selected date range.</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AgentPerformanceReport;
